/**
 * Hook to manage row operations for ViewGrid
 *
 * Provides new row creation state and handlers.
 * Based on original MyVault useRowOperations.ts patterns.
 */

import { useCallback, useMemo, useState } from "react";
import type { ViewColumn, ProcessedViewData } from "../../services/viewService";
import { useCreateRowMutation } from "../../hooks/useViewDataQuery";

interface UseRowOperationsProps {
  viewId: string | undefined;
  data: ProcessedViewData | undefined;
  onCreateRow?: (data: Record<string, unknown>) => Promise<void>;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

interface UseRowOperationsReturn {
  newRowData: Record<string, unknown> | null;
  isCreating: boolean;
  handleAddNewRow: () => void;
  handleSaveNewRow: (
    rowDataOverride?: Record<string, unknown>,
  ) => Promise<void>;
  handleCancelNewRow: () => void;
  handleNewRowChange: (field: string, value: unknown) => void;
  rowsWithNew: Array<Record<string, unknown>>;
}

/**
 * Initialize an empty row with default values based on column types
 */
function initializeEmptyRow(columns: ViewColumn[]): Record<string, unknown> {
  const row: Record<string, unknown> = {
    _rowId: "__new_row__",
  };

  for (const col of columns) {
    // Skip non-editable and identifier columns
    if (col.editMode === "READONLY" || col.field.endsWith(".id")) {
      continue;
    }

    // Set default values based on type
    switch (col.type) {
      case "boolean":
        row[col.field] = false;
        break;
      case "number":
        row[col.field] = null;
        break;
      case "date":
      case "dateTime":
        row[col.field] = null;
        break;
      default:
        row[col.field] = "";
    }
  }

  return row;
}

/**
 * Filter row data for create operation
 * Removes internal fields and read-only columns
 */
function filterFieldsForCreate(
  rowData: Record<string, unknown>,
  columns: ViewColumn[],
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  const editableFields = new Set(
    columns
      .filter((c) => c.editMode !== "READONLY" && !c.field.endsWith(".id"))
      .map((c) => c.field),
  );

  for (const [key, value] of Object.entries(rowData)) {
    // Skip internal fields
    if (key.startsWith("_") || key === "id") continue;
    // Only include editable fields
    if (editableFields.has(key) && value !== null && value !== "") {
      result[key] = value;
    }
  }

  return result;
}

export function useRowOperations({
  viewId,
  data,
  onCreateRow,
  onSuccess,
  onError,
}: UseRowOperationsProps): UseRowOperationsReturn {
  const [newRowData, setNewRowData] = useState<Record<string, unknown> | null>(
    null,
  );
  const createRowMutation = useCreateRowMutation();

  // Compute rows with new row prepended
  const rowsWithNew = useMemo(() => {
    const existingRows = data?.rows || [];
    if (newRowData) {
      return [newRowData, ...existingRows];
    }
    return existingRows;
  }, [data, newRowData]);

  // Handle add new row
  const handleAddNewRow = useCallback(() => {
    if (!data?.columns) return;
    const emptyRow = initializeEmptyRow(data.columns);
    setNewRowData(emptyRow);
  }, [data?.columns]);

  // Handle new row field change
  const handleNewRowChange = useCallback((field: string, value: unknown) => {
    setNewRowData((prev) => {
      if (!prev) return null;
      return { ...prev, [field]: value };
    });
  }, []);

  // Handle save new row
  // rowDataOverride allows passing the current row data from the grid API
  // to handle the case where cell edits haven't been committed to state yet
  const handleSaveNewRow = useCallback(
    async (rowDataOverride?: Record<string, unknown>) => {
      if (!viewId || !data?.columns) return;

      // Use override data if provided (from grid API), otherwise fall back to state
      const rowToSave = rowDataOverride || newRowData;
      if (!rowToSave) return;

      const createData = filterFieldsForCreate(rowToSave, data.columns);

      try {
        if (onCreateRow) {
          await onCreateRow(createData);
        } else {
          await createRowMutation.mutateAsync({ viewId, data: createData });
        }
        onSuccess?.("Record created successfully");
        setNewRowData(null);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        onError?.(`Failed to create record: ${errorMessage}`);
      }
    },
    [
      viewId,
      newRowData,
      data?.columns,
      createRowMutation,
      onCreateRow,
      onSuccess,
      onError,
    ],
  );

  // Handle cancel new row
  const handleCancelNewRow = useCallback(() => {
    setNewRowData(null);
  }, []);

  return {
    newRowData,
    isCreating: createRowMutation.isPending,
    handleAddNewRow,
    handleSaveNewRow,
    handleCancelNewRow,
    handleNewRowChange,
    rowsWithNew,
  };
}
